import { css } from 'styled-components';
import type { GlobalToken } from 'antd';

export type InputStatus = 'default' | 'warning' | 'error' | 'success';

/**
 * Shared status styles for form inputs (Input, TextArea, Select)
 */
export const getInputStatusStyles = (token: GlobalToken, status: InputStatus) => {
  const statusMap = {
    default: css`
      background: ${token.colorBgContainer};
      border-color: ${token.colorBorder};

      &:hover:not(:disabled) {
        border-color: ${token.colorPrimaryHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorPrimary};
        box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
      }
    `,
    warning: css`
      background: ${token.colorWarningBg};
      border-color: ${token.colorWarning};

      &:hover:not(:disabled) {
        border-color: ${token.colorWarningHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorWarning};
        box-shadow: 0 0 0 2px ${token.colorWarningBg};
      }
    `,
    error: css`
      background: ${token.colorErrorBg};
      border-color: ${token.colorError};

      &:hover:not(:disabled) {
        border-color: ${token.colorErrorHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorError};
        box-shadow: 0 0 0 2px ${token.colorErrorBg};
      }
    `,
    success: css`
      background: ${token.colorSuccessBg};
      border-color: ${token.colorSuccess};

      &:hover:not(:disabled) {
        border-color: ${token.colorSuccessHover};
      }

      &:focus,
      &.ant-input-focused {
        border-color: ${token.colorSuccess};
        box-shadow: 0 0 0 2px ${token.colorSuccessBg};
      }
    `,
  };
  return statusMap[status];
};

/**
 * Shared status styles for pickers (DatePicker, TimePicker)
 * Uses .ant-picker selector structure
 */
export const getPickerStatusStyles = (token: GlobalToken, status: InputStatus) => {
  const statusMap = {
    default: css`
      &.ant-picker {
        background: ${token.colorBgContainer};
        border-color: ${token.colorBorderSecondary};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorPrimaryHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorPrimary};
          box-shadow: 0 0 0 2px ${token.colorPrimaryBg};
        }
      }
    `,
    warning: css`
      &.ant-picker {
        background: ${token.colorWarningBg};
        border-color: ${token.colorWarning};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorWarningHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorWarning};
          box-shadow: 0 0 0 2px ${token.colorWarningBg};
        }
      }
    `,
    error: css`
      &.ant-picker {
        background: ${token.colorErrorBg};
        border-color: ${token.colorError};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorErrorHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorError};
          box-shadow: 0 0 0 2px ${token.colorErrorBg};
        }
      }
    `,
    success: css`
      &.ant-picker {
        background: ${token.colorSuccessBg};
        border-color: ${token.colorSuccess};

        &:hover:not(.ant-picker-disabled) {
          border-color: ${token.colorSuccessHover};
        }

        &.ant-picker-focused {
          border-color: ${token.colorSuccess};
          box-shadow: 0 0 0 2px ${token.colorSuccessBg};
        }
      }
    `,
  };
  return statusMap[status];
};

// Alias for backwards compatibility
export const getStatusStyles = getInputStatusStyles;

/**
 * Get help text color based on status
 */
export const getHelpTextColor = (token: GlobalToken, status: InputStatus) => {
  switch (status) {
    case 'error':
      return token.colorError;
    case 'warning':
      return token.colorWarning;
    case 'success':
      return token.colorSuccess;
    default:
      return token.colorTextTertiary;
  }
};
